// src/components/HRComponents/PolicyDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, AlertTriangle, FileEdit, CheckCircle, Archive, ArrowRight, Calendar } from 'lucide-react';
import { toast } from 'react-toastify';

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import { formatDateTime } from '../../helpers/formatDateTime';

const PolicyDashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ draft: 0, pending_approval: 0, published: 0, archived: 0 });
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch both dashboard counts summary and expiring policies in parallel
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, expiringRes] = await Promise.all([
        api.get(SummaryApi.getPolicyDashboardSummary.url),
        api.get(SummaryApi.getPolicyDashboardExpiring.url),
      ]);

      if (summaryRes.data.success) {
        setSummary(summaryRes.data.counts || { draft: 0, pending_approval: 0, published: 0, archived: 0 });
      }
      if (expiringRes.data.success) {
        setExpiringDocs(expiringRes.data.documents || []);
      }
    } catch (error) {
      toast.error('Failed to load dashboard metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  if (loading) {
    return <div className="p-8 text-center text-gray-400">Loading Dashboard...</div>;
  }

  // Calculate total documents across all statuses
  const totalDocuments = summary.draft + summary.pending_approval + summary.published + summary.archived;

  return (
    <div className="p-4 space-y-6">
      {/* Visual Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white border rounded-xl p-4 flex items-center justify-between col-span-2 lg:col-span-1 shadow-sm">
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Total Documents</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{totalDocuments}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
            <FileText className="h-5 w-5 text-red-500" />
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Published</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{summary.published}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
            <CheckCircle className="h-5 w-5 text-green-500" />
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Drafts</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{summary.draft}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
            <FileEdit className="h-5 w-5 text-gray-500" />
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Pending Approval</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{summary.pending_approval}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-yellow-50 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Archived</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{summary.archived}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
            <Archive className="h-5 w-5 text-red-500" />
          </div>
        </div>
      </div>

      {/* Main Expiring Soon Policies Section */}
      <div className="bg-white border rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 pb-4 border-b mb-4">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          <div>
            <h2 className="text-lg font-bold text-gray-900">Policies Expiring Soon</h2>
            <p className="text-xs text-gray-500">Documents approaching their renewal reminder cycles that require active review</p>
          </div>
        </div>

        {expiringDocs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Great job! No policies or letters are expiring soon.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                  <th className="py-3 px-4">Document Details</th>
                  <th className="py-3 px-4">Code</th>
                  <th className="py-3 px-4">Expiry Date</th>
                  <th className="py-3 px-4">Reminder Range</th>
                  <th className="py-3 px-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y text-sm">
                {expiringDocs.map((doc) => (
                  <tr key={doc._id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3.5 px-4 font-semibold text-gray-900">{doc.title}</td>
                    <td className="py-3.5 px-4 text-gray-500 font-medium">{doc.policyCode}</td>
                    <td className="py-3.5 px-4 text-red-600 font-semibold">
                      {formatDateTime(doc.expiryDate, false)}
                    </td>
                    <td className="py-3.5 px-4 text-gray-600 font-medium">{doc.renewalReminderDays} days notice</td>
                    <td className="py-3.5 px-4 text-center">
                      <button
                        onClick={() => navigate(`../policies-letters/view/${doc._id}`)}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                      >
                        View & Renew <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyDashboard;